// useSettingsNav — the tree module 05's `SettingsNavTree` renders (contract 22 §9, CM-22-04).
// It is the ONLY reader of the nav registry: entries arrive from five other modules, so every
// predicate is evaluated defensively and 22 never learns whose entry it is showing.
//
// Component-scoped: `isActive` reads the current route through `useRoute()`.
import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

import { settingsNavEntries } from '../flows/settingsNavRegistry'
import {
  SETTINGS_GROUP_KEYS,
  settingsGroupLabelKey,
  type SettingsNavEntry,
  type SettingsNavGroup,
} from '../models/settingsNav.types'

export interface UseSettingsNav {
  /** Every visible entry, group order then `order` — the flat form `firstVisibleEntry` reads. */
  entries: ComputedRef<SettingsNavEntry[]>
  /** The same list grouped; empty groups are dropped so the tree renders no bare headings. */
  groups: ComputedRef<SettingsNavGroup[]>
  /** The `/settings` index target — `null` when the user can see nothing at all (§14). */
  firstVisibleEntry: ComputedRef<SettingsNavEntry | null>
  isActive: (key: string) => boolean
}

/** §14: a registrant whose RBAC has not hydrated yet hides its OWN entry, never breaks the nav. */
function isVisible(entry: SettingsNavEntry): boolean {
  try {
    return entry.visible()
  } catch {
    return false
  }
}

/**
 * The visible entries in render order — group order first, then `order` within a group. Pure and
 * route-free so the `?selected_tab` shim can ask for the landing pane outside a component scope
 * (ORP-19899); `entries` below is this list, made reactive.
 */
export function orderedVisibleSettingsEntries(): SettingsNavEntry[] {
  return SETTINGS_GROUP_KEYS.flatMap((group) =>
    settingsNavEntries()
      .filter((entry) => entry.group === group && isVisible(entry))
      .sort((a, b) => a.order - b.order),
  )
}

export function useSettingsNav(): UseSettingsNav {
  const route = useRoute()

  const entries = computed(orderedVisibleSettingsEntries)

  const groups = computed<SettingsNavGroup[]>(() =>
    SETTINGS_GROUP_KEYS.map((key) => ({
      key,
      labelKey: settingsGroupLabelKey(key),
      entries: entries.value.filter((entry) => entry.group === key),
    })).filter((group) => group.entries.length > 0),
  )

  const firstVisibleEntry = computed<SettingsNavEntry | null>(() => entries.value[0] ?? null)

  /** Active state comes from the route, never from stored tab state (CM-22-04: the URL IS the
   *  state). `meta.navKey` is preferred so a registrant can rename its route without touching 22. */
  const isActive = (key: string): boolean => {
    const entry = settingsNavEntries().find((candidate) => candidate.key === key)
    if (!entry) return false
    return route.meta.navKey === key || route.name === entry.routeName
  }

  return { entries, groups, firstVisibleEntry, isActive }
}
